import React, { useState } from 'react';
import ReactDOM from 'react-dom';

// Clock Dial Time Picker Component with popup using Portal
const ClockTimePicker = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('hour'); // 'hour' or 'minute'
  const [popupPosition, setPopupPosition] = useState(null);
  const buttonRef = React.useRef(null);
  const popupRef = React.useRef(null);
  
  // Open handler that calculates position immediately
  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + (rect.width / 2)
      });
    }
    setMode('hour');
    setIsOpen(true);
  };
  
  const handleClose = () => {
    setIsOpen(false);
    setPopupPosition(null);
  };
  
  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Parse value (HH:MM format) into hour, minute, period
  const parseTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') {
      return { hour: 9, minute: 0, period: 'AM' };
    }
    const parts = timeStr.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return {
      hour: h % 12 || 12,
      minute: m,
      period: h >= 12 ? 'PM' : 'AM'
    };
  };
  
  const { hour, minute, period } = parseTime(value);
  
  // Format for display (12h format)
  const formatDisplay = () => {
    return `${hour}:${String(minute).padStart(2, '0')} ${period}`;
  };
  
  // Convert back to 24h format
  const formatTime = (h, m, p) => {
    let hour24 = h % 12;
    if (p === 'PM') hour24 += 12;
    if (h === 12 && p === 'AM') hour24 = 0;
    if (h === 12 && p === 'PM') hour24 = 12;
    return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  
  const handleHourClick = (h) => {
    onChange(formatTime(h, minute, period));
    setMode('minute');
  };
  
  const handleMinuteClick = (m) => {
    onChange(formatTime(hour, m, period));
    handleClose();
  };
  
  const togglePeriod = (p) => {
    onChange(formatTime(hour, minute, p));
  };
  
  // Calculate position on clock face
  const getPosition = (value, total, radius = 70) => {
    const angle = (value / total) * 360 - 90;
    const rad = (angle * Math.PI) / 180;
    return {
      x: 90 + radius * Math.cos(rad),
      y: 90 + radius * Math.sin(rad)
    };
  };
  
  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  
  return (
    <div style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
        {label}
      </label>
      
      {/* Time button that opens the clock */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => isOpen ? handleClose() : handleOpen()}
        style={{
          padding: '6px 10px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '0.8rem',
          background: 'white',
          color: '#334155',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'border-color 0.15s ease'
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span style={{ color: '#334155' }}>{formatDisplay()}</span>
      </button>
      
      {/* Popup clock dial using Portal */}
      {isOpen && popupPosition && ReactDOM.createPortal(
        <div 
          ref={popupRef}
          style={{
            position: 'absolute',
            top: popupPosition.top,
            left: popupPosition.left,
            transform: 'translateX(-50%)',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            padding: '16px',
            zIndex: 'var(--z-modal-popover, 1000001)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          {/* Digital display */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px'
          }}>
            <button
              type="button"
              onClick={() => setMode('hour')}
              style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: mode === 'hour' ? '#185FA5' : '#64748b',
                background: mode === 'hour' ? '#E6F1FB' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                padding: '4px 8px',
                cursor: 'pointer',
                minWidth: '44px'
              }}
            >
              {String(hour).padStart(2, '0')}
            </button>
            <span style={{ fontSize: '1.5rem', fontWeight: 600, color: '#64748b' }}>:</span>
            <button
              type="button"
              onClick={() => setMode('minute')}
              style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: mode === 'minute' ? '#185FA5' : '#64748b',
                background: mode === 'minute' ? '#E6F1FB' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                padding: '4px 8px',
                cursor: 'pointer',
                minWidth: '44px'
              }}
            >
              {String(minute).padStart(2, '0')}
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '8px' }}>
              <button
                type="button"
                onClick={() => togglePeriod('AM')}
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: 'none',
                  background: period === 'AM' ? '#185FA5' : '#e2e8f0',
                  color: period === 'AM' ? 'white' : '#64748b',
                  cursor: 'pointer'
                }}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => togglePeriod('PM')}
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: 'none',
                  background: period === 'PM' ? '#185FA5' : '#e2e8f0',
                  color: period === 'PM' ? 'white' : '#64748b',
                  cursor: 'pointer'
                }}
              >
                PM
              </button>
            </div>
          </div>
          
          {/* Clock face */}
          <div style={{
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: '#f8fafc',
            border: '2px solid #e2e8f0',
            position: 'relative',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
          }}>
            {/* Center dot */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#185FA5',
              zIndex: 2
            }} />
            
            {/* Clock hand */}
            {mode === 'hour' ? (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '2px',
                height: '50px',
                background: '#185FA5',
                transformOrigin: 'top center',
                transform: `rotate(${(hour % 12) * 30 + 180}deg)`,
                borderRadius: '1px',
                zIndex: 1
              }} />
            ) : (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '2px',
                height: '65px',
                background: '#185FA5',
                transformOrigin: 'top center',
                transform: `rotate(${minute * 6 + 180}deg)`,
                borderRadius: '1px',
                zIndex: 1
              }} />
            )}
            
            {/* Hour/Minute numbers */}
            {mode === 'hour' ? (
              hours.map((h) => {
                const pos = getPosition(h === 12 ? 0 : h, 12, 68);
                const isSelected = hour === h;
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() => handleHourClick(h)}
                    style={{
                      position: 'absolute',
                      left: `${pos.x}px`,
                      top: `${pos.y}px`,
                      transform: 'translate(-50%, -50%)',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: 'none',
                      background: isSelected ? '#185FA5' : 'transparent',
                      color: isSelected ? 'white' : '#334155',
                      fontSize: '0.8rem',
                      fontWeight: isSelected ? 600 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {h}
                  </button>
                );
              })
            ) : (
              minutes.map((m) => {
                const pos = getPosition(m, 60, 68);
                const isSelected = minute === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleMinuteClick(m)}
                    style={{
                      position: 'absolute',
                      left: `${pos.x}px`,
                      top: `${pos.y}px`,
                      transform: 'translate(-50%, -50%)',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: 'none',
                      background: isSelected ? '#185FA5' : 'transparent',
                      color: isSelected ? 'white' : '#334155',
                      fontSize: '0.75rem',
                      fontWeight: isSelected ? 600 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {String(m).padStart(2, '0')}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ClockTimePicker;